import { useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ForeplayProviderBalance } from "./foreplay-provider-balance";
import {
  canEditForeplayReview,
  canStartForeplayJob,
  createForeplayExpansionApi,
  type ForeplayCandidate,
  type ForeplayJobDetail,
} from "@/components/admin/foreplay-expansion-api";

const api = createForeplayExpansionApi();
const detailKey = (id: number) => ["foreplay-harvest-job", id];

export function SeederForeplayExpansionPanel({ jobId }: { jobId: number }) {
  const queryClient = useQueryClient();
  const [reasons, setReasons] = useState<Record<number, string>>({});
  const { data: detail, isLoading, error } = useQuery<ForeplayJobDetail>({
    queryKey: detailKey(jobId),
    queryFn: () => api.job(jobId),
    refetchInterval: (query) => {
      const status = (query.state.data as ForeplayJobDetail | undefined)?.job.status;
      return ["queued", "running", "discovering", "scanning", "cancelling"].includes(status ?? "") ? 2000 : false;
    },
  });
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: detailKey(jobId) }),
      queryClient.invalidateQueries({ queryKey: ["foreplay-harvest-jobs"] }),
    ]);
  };
  const review = useMutation({
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: detailKey(jobId) });
    },
    mutationFn: ({ candidate, patch }: {
      candidate: ForeplayCandidate;
      patch: { classification?: "relevant" | "irrelevant" | "uncertain"; reason?: string | null; approved?: boolean };
    }) => api.review(jobId, candidate.id, patch),
    onSuccess: ({ candidate }, { candidate: reviewedCandidate, patch }) => {
      queryClient.setQueryData<ForeplayJobDetail>(detailKey(jobId), (current) => current && ({
        ...current,
        candidates: current.candidates.map((item) => item.id === candidate.id ? candidate : item),
      }));
      if (Object.hasOwn(patch, "reason")) {
        setReasons((old) => ({ ...old, [reviewedCandidate.id]: candidate.reason ?? "" }));
      }
      // The PATCH response is authoritative for this row. Mark the detail stale
      // without immediately replacing it with a refetch, and let the jobs list
      // refresh separately so the saved review unlocks approval right away.
      void queryClient.invalidateQueries({ queryKey: detailKey(jobId), refetchType: "none" });
      void queryClient.invalidateQueries({ queryKey: ["foreplay-harvest-jobs"] });
    },
  });
  const action = useMutation({
    mutationFn: (kind: "start" | "resume" | "cancel") => api[kind](jobId),
    onSuccess: refresh,
  });

  if (isLoading) return <div className="p-6 text-sm text-[#526074]">Loading Foreplay job…</div>;
  if (error || !detail) return <div role="alert" className="m-6 text-sm text-red-700">Could not load Foreplay job: {error instanceof Error ? error.message : "No job returned"}</div>;

  const { job, candidates } = detail;
  const active = ["queued", "running", "discovering", "scanning", "cancelling"].includes(job.status);
  const resumable = ["interrupted", "partial", "failed"].includes(job.status);
  const reviewReady = canStartForeplayJob(candidates);
  const actionError = action.error instanceof Error ? action.error.message : null;

  return (
    <div className="p-6 space-y-5">
      <section className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Foreplay job #{job.id}
            <span className="ml-3 rounded bg-blue-50 px-2 py-0.5 text-xs">{job.status.replace(/_/g, " ")}</span>
          </h1>
          <p className="mt-1 text-xs text-[#526074]">Created {new Date(job.createdAt).toLocaleString()} · Updated {new Date(job.updatedAt).toLocaleString()}</p>
          {job.error && <p role="alert" className="mt-2 text-sm text-red-700">{job.error}</p>}
          {job.foreplayCheckpoint != null && <p className="mt-2 text-xs text-[#526074]">Saved checkpoint: {JSON.stringify(job.foreplayCheckpoint)}</p>}
        </div>
        <div className="flex gap-2">
          {resumable && <Button variant="outline" disabled={action.isPending || review.isPending} onClick={() => action.mutate("resume")}>Resume</Button>}
          {!active && job.status === "draft" && <Button disabled={!reviewReady || action.isPending || review.isPending} onClick={() => action.mutate("start")}>Start approved harvest</Button>}
          {active && <Button variant="outline" disabled={action.isPending} onClick={() => action.mutate("cancel")}>Request cancel</Button>}
        </div>
      </section>

      <ForeplayProviderBalance />
      <section className="rounded border border-[#DCE3ED] p-3 text-xs text-[#526074]" data-testid="foreplay-provider-status">
        Foreplay checks credits when each request runs. Only relevant candidates you approve will be harvested.
        {actionError && <span role="alert" className="ml-2 text-red-700">{actionError}</span>}
        {action.isPending && <Loader2 className="ml-2 inline h-3 w-3 animate-spin" />}
      </section>

      <section>
        <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
          <div><h2 className="font-semibold">Candidate review</h2><p className="text-xs text-[#526074]">Only relevant candidates you explicitly approve will be harvested.</p></div>
          <div className="text-xs text-[#526074]">{candidates.filter((c) => c.scanStatus === "completed").length}/{candidates.length} scanned · {candidates.reduce((sum, c) => sum + (c.adsScraped ?? 0), 0)} ads</div>
        </div>
        {review.error instanceof Error && <p role="alert" className="mb-2 text-sm text-red-700">Could not save candidate review: {review.error.message}</p>}
        {candidates.map((candidate) => (
          <CandidateReviewRow key={candidate.id} candidate={candidate} reason={reasons[candidate.id] ?? candidate.reason ?? ""}
            onReason={(value) => setReasons((old) => ({ ...old, [candidate.id]: value }))}
            onSaveReason={(value) => review.mutate({ candidate, patch: { reason: value.trim() || null } })}
            onClassification={(classification) => review.mutate({ candidate, patch: { classification } })}
            onApproval={(approved) => review.mutate({ candidate, patch: { approved } })}
            busy={review.isPending} disabled={!canEditForeplayReview(job.status) || action.isPending} />
        ))}
        {!reviewReady && <p className="mt-2 text-xs text-amber-800">Complete classifications and approve each relevant candidate to enable start.</p>}
      </section>

      <ForeplayCoverage />
    </div>
  );
}

function CandidateReviewRow({ candidate, reason, onReason, onSaveReason, onClassification, onApproval, busy, disabled }: {
  candidate: ForeplayCandidate; reason: string; onReason: (value: string) => void;
  onSaveReason: (value: string) => void;
  onClassification: (classification: "relevant" | "irrelevant" | "uncertain") => void;
  onApproval: (approved: boolean) => void; busy: boolean; disabled: boolean;
}) {
  const classifications = ["relevant", "uncertain", "irrelevant"] as const;
  return (
    <div className="mb-2 grid gap-2 rounded border border-[#DCE3ED] p-3 md:grid-cols-[minmax(140px,1fr)_150px_minmax(140px,1fr)_auto] md:items-center" data-testid={`foreplay-candidate-${candidate.id}`}>
      <div><div className="text-sm font-medium">{candidate.brandName}</div><div className="text-xs text-[#526074]">{candidate.niche || "Unassigned niche"} · {candidate.scanStatus}{candidate.adsScraped != null ? ` · ${candidate.adsScraped} ads` : ""}</div>{candidate.scanError && <div className="text-xs text-red-700">{candidate.scanError}</div>}</div>
      <select aria-label={`Classification for ${candidate.brandName}`} className="rounded border px-2 py-1 text-sm" value={candidate.classification ?? ""} disabled={busy || disabled} onChange={(event) => {
        const classification = event.target.value as typeof classifications[number];
        onClassification(classification);
      }}>
        <option value="" disabled>Classify…</option>{classifications.map((value) => <option key={value} value={value}>{value}</option>)}
      </select>
      <div className="flex min-w-0 gap-2">
        <input aria-label={`Review reason for ${candidate.brandName}`} className="min-w-0 flex-1 rounded border px-2 py-1 text-sm" placeholder="Reason (optional)" value={reason} disabled={busy || disabled} onChange={(event) => onReason(event.target.value)} />
        <Button size="sm" variant="outline" disabled={busy || disabled || reason === (candidate.reason ?? "")} onClick={() => onSaveReason(reason)}>Save reason</Button>
      </div>
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={candidate.approved} disabled={busy || disabled || candidate.classification !== "relevant"} onChange={(event) => onApproval(event.target.checked)} />Approve</label>
      {busy && <Loader2 className="h-4 w-4 animate-spin" />}
    </div>
  );
}

function ForeplayCoverage() {
  const [niche, setNiche] = useState("");
  const { data, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["foreplay-approved-coverage", niche],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => api.coverage(niche.trim() || undefined, pageParam),
    getNextPageParam: (lastPage) => lastPage.page.hasMore ? lastPage.page.nextAfterId ?? undefined : undefined,
    staleTime: 30_000,
  });
  const firstPage = data?.pages[0];
  const ads = data?.pages.flatMap((page) => page.ads) ?? [];
  return (
    <section className="border-t border-[#DCE3ED] pt-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div><h2 className="font-semibold">Approved Foreplay coverage</h2><p className="text-xs text-[#526074]">Only promoted, non-rejected ads appear here. Candidate classifications are not library approvals.</p></div>
        <div className="flex items-center gap-2"><input aria-label="Filter approved coverage by niche" className="rounded border px-2 py-1 text-sm" placeholder="Filter niche" value={niche} onChange={(e) => setNiche(e.target.value)} />{isFetching && <Loader2 className="h-4 w-4 animate-spin" />}</div>
      </div>
      {error && <p role="alert" className="text-sm text-red-700">Could not load approved coverage.</p>}
      {firstPage && <><p className="mb-2 text-xs text-[#526074]">{firstPage.approvedCount.toLocaleString()} approved ads · {firstPage.diversity.creativeCount.toLocaleString()} creatives · {firstPage.diversity.brandCount.toLocaleString()} brands · {firstPage.diversity.angleCount.toLocaleString()} angles · {firstPage.diversity.styleCount.toLocaleString()} styles · showing {ads.length.toLocaleString()} records{hasNextPage ? " (more available)" : ""}</p><div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{ads.map((ad) => <article key={ad.id} className="overflow-hidden rounded border border-[#DCE3ED]">
        {ad.imageUrl && <img src={ad.imageUrl} alt="" className="h-36 w-full object-cover" loading="lazy" />}
        <div className="p-2 text-xs"><strong>{ad.sourceBrand}</strong><div>{ad.niche || "Unassigned niche"} · {ad.daysLive == null ? "Days live unknown" : `${ad.daysLive} days live`}</div><div>Analysis: {ad.analysisStatus}</div>{ad.sourceUrl && <a className="text-indigo-700 underline" href={ad.sourceUrl} target="_blank" rel="noreferrer">Source</a>}</div>
      </article>)}</div>{hasNextPage && <Button className="mt-3" size="sm" variant="outline" disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>{isFetchingNextPage ? "Loading approved ads…" : "Load more approved ads"}</Button>}</>}
    </section>
  );
}
